import React, { useEffect, useState, useRef, useMemo } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { BsImage, BsX } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { PURPLE, hexToRgba } from '../../utils/designUtils';

function PSection({ title, required, action, children }) {
    return (
        <section style={{ background: 'white', borderRadius: 16, border: '1px solid #f0eff5', boxShadow: '0 1px 2px rgba(60,50,120,.04), 0 4px 16px -8px rgba(60,50,120,.08)', overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ padding: '14px 22px', background: 'linear-gradient(180deg, #faf9fd 0%, #f5f4fa 100%)', borderBottom: '1px solid #ececf2', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <h3 style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: '#1f1d2e', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {title}
                    {required && <span style={{ color: '#d97757', fontSize: 14 }}>*</span>}
                </h3>
                {action}
            </div>
            <div style={{ padding: '20px 22px' }}>{children}</div>
        </section>
    );
}

function PRow({ label, required, children, hint }) {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', alignItems: 'flex-start', gap: 16, padding: '10px 0', borderBottom: '1px solid #f8f7fc' }}>
            <label style={{ paddingTop: 12, fontSize: 13, color: '#3a3849', fontWeight: 600 }}>
                {label}
                {required && <span style={{ color: '#d97757', marginLeft: 4 }}>*</span>}
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {children}
                {hint && <span style={{ fontSize: 11.5, color: '#8b8a99' }}>{hint}</span>}
            </div>
        </div>
    );
}

const MAX_TITLE_BYTES = 50;
const MAX_BYTES = 3000;

const Block = Quill.import('blots/block');
Block.tagName = 'DIV';
Quill.register(Block, true);

const getPlainText = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
};

const getByteLength = (str) => {
    if (!str) return 0;
    return new Blob([str]).size;
};

const TextEditor = ({
    initialContent, title, onTitleChange, onExtDataChange,
    templateList, handleImageChange, imagePreviewUrl, setPreviewUrls,
    isLocked, templateId, onTemplateModified, onTemplateSelect, enableTemplateSelect,
}) => {
    const [content, setContent] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [imageUrl, setImageUrl] = useState(initialContent?.imageUrl || '');
    const quillRef = useRef(null);
    const isInitialLoaded = useRef(false);
    const isTemplateModifiedRef = useRef(false);
    const prevContentRef = useRef('');

    useEffect(() => {
        if (templateId) isTemplateModifiedRef.current = false;
    }, [templateId]);

    const handleTemplateModification = () => {
        if (templateId && !isTemplateModifiedRef.current) {
            isTemplateModifiedRef.current = true;
            onTemplateModified?.();
            toast.info('템플릿 연결이 해제되었습니다.');
        }
    };

    useEffect(() => {
        if (isInitialLoaded.current || !initialContent) return;
        const { body, imageUrl } = initialContent;
        setImageUrl(imageUrl || '');
        if (body) {
            setContent(body);
            prevContentRef.current = body;
            isInitialLoaded.current = true;
        }
    }, [initialContent]);

    const titleBytes = useMemo(() => getByteLength(title || ''), [title]);
    const bodyBytes = useMemo(() => getByteLength(getPlainText(content)), [content]);
    const handleContentChange = (value, delta, source, editor) => {
        const text = editor.getText();
        const bytes = getByteLength(text);
        if (bytes > MAX_BYTES) {
            toast.warning('메시지는 3,000 bytes까지 입력할 수 있습니다.');
            const quill = quillRef.current?.getEditor();
            if (quill) quill.setText(getPlainText(prevContentRef.current) || '');
            return;
        }
        const plainText = editor.getText().replace(/\n$/, '');
        if (templateId && !isTemplateModifiedRef.current) {
            const currentPlain = getPlainText(prevContentRef.current);
            if (currentPlain !== plainText) handleTemplateModification();
        }
        prevContentRef.current = value;
        setContent(value);
        onExtDataChange?.('body', value);
    };

    const handleTitleChange = (e) => {
        const newValue = e.target.value;
        if (newValue !== title) handleTemplateModification();
        onTitleChange?.(newValue);
    };

    const handleLoadTemplate = (template) => {
        onTemplateSelect?.(template.id);
        isTemplateModifiedRef.current = false;
        setShowModal(false);
    };

    const handleImageUrlChange = (e) => {
        const url = e.target.value;
        if (url !== imageUrl) handleTemplateModification();
        setImageUrl(url);
        setPreviewUrls?.(url);
        onExtDataChange?.('imageUrl', url);
        if (url && url.startsWith('data:')) {
            setTimeout(() => {
                toast.error('Base64 형식의 이미지는 사용할 수 없습니다. URL을 입력하거나 파일을 업로드해주세요.');
                setImageUrl('');
                setPreviewUrls?.('');
                onExtDataChange?.('imageUrl', '');
            }, 300);
        }
    };

    const modules = useMemo(() => ({
        toolbar: {
            container: [
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                ['undo', 'redo'],
            ],
            handlers: {
                undo: function() { this.quill.history.undo(); },
                redo: function() { this.quill.history.redo(); }
            }
        },
        clipboard: { matchVisual: false },
        history: { delay: 1000, maxStack: 50, userOnly: true }
    }), []);

    useEffect(() => {
        const quill = quillRef.current?.getEditor();
        if (quill) {
            const Block = quill.constructor.import('blots/block');
            Block.tagName = 'DIV';
            quill.constructor.register(Block, true);
        }
    }, []);

    const formats = ['bold', 'italic', 'underline', 'strike', 'list', 'bullet'];

    const inp = {
        height: 42, borderRadius: 10, padding: '0 14px', width: '100%',
        border: '1.5px solid #ececf2', background: isLocked ? '#f5f4fa' : 'white',
        fontSize: 13.5, color: '#1f1d2e', outline: 'none', fontFamily: 'inherit',
        transition: 'border-color 140ms, box-shadow 140ms', boxSizing: 'border-box',
    };

    const focusOn = (e) => {
        e.target.style.borderColor = PURPLE;
        e.target.style.boxShadow = `0 0 0 4px ${hexToRgba(PURPLE, .10)}`;
    };
    const focusOff = (e) => {
        e.target.style.borderColor = '#ececf2';
        e.target.style.boxShadow = 'none';
    };

    const templateLoadBtn = enableTemplateSelect !== false ? (
        <button
            onClick={() => setShowModal(true)}
            disabled={isLocked}
            style={{
                height: 30, padding: '0 12px', borderRadius: 8,
                background: 'white', border: `1.5px solid ${PURPLE}`,
                color: PURPLE, fontSize: 12, fontWeight: 600,
                cursor: isLocked ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 5,
                opacity: isLocked ? 0.5 : 1,
            }}
        >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <path d="M14 2v6h6M8 13h8M8 17h5"/>
            </svg>
            템플릿 불러오기
        </button>
    ) : null;

    return (
        <div className="editor-and-preview-container">
            <PSection title="메시지 내용" action={templateLoadBtn}>
                <PRow label="푸시 제목" required>
                    <input
                        type="text"
                        placeholder="푸시 알림에 표시될 제목을 입력하세요"
                        value={title || ''}
                        onChange={handleTitleChange}
                        disabled={isLocked}
                        style={inp}
                        onFocus={focusOn}
                        onBlur={focusOff}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: titleBytes > MAX_TITLE_BYTES ? '#c44569' : '#8b8a99' }}>
                        <span>{titleBytes > MAX_TITLE_BYTES ? '초과된 내용은 잘릴 수 있습니다' : ''}</span>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                            <b style={{ color: titleBytes > MAX_TITLE_BYTES ? '#c44569' : '#3a3849' }}>{titleBytes}</b> / {MAX_TITLE_BYTES} bytes
                        </span>
                    </div>
                </PRow>

                <PRow label="푸시 내용" required>
                    <style>{`
                        .push-quill-editor .ql-container { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; font-size: 1rem; border-radius: 0 0 10px 10px; border-color: #ececf2; }
                        .push-quill-editor .ql-toolbar { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; border-radius: 10px 10px 0 0; border-color: #ececf2; background: #fafaff; }
                        .push-quill-editor .ql-editor { min-height: 160px; max-height: 300px; font-family: inherit; font-size: 1rem; line-height: 1.6; }
                        .push-quill-editor .ql-editor * { font-family: inherit !important; font-size: 1rem !important; }
                        .push-quill-editor .ql-editor p { margin-bottom: 0.5rem; }
                        .push-quill-editor.disabled .ql-toolbar { display: none; }
                        .push-quill-editor.disabled .ql-container { background-color: #f5f4fa; border-radius: 10px; }
                        .push-quill-editor .ql-undo, .push-quill-editor .ql-redo { width: 28px !important; }
                        .push-quill-editor .ql-undo::before { content: '↶'; font-size: 16px; }
                        .push-quill-editor .ql-redo::before { content: '↷'; font-size: 16px; }
                    `}</style>
                    <div className={`push-quill-editor ${isLocked ? 'disabled' : ''}`}>
                        <ReactQuill
                            ref={quillRef}
                            theme="snow"
                            value={content}
                            onChange={handleContentChange}
                            modules={{ ...modules, clipboard: { matchVisual: false } }}
                            formats={formats}
                            readOnly={isLocked}
                            placeholder="푸시 내용을 입력하세요"
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: bodyBytes > MAX_BYTES ? '#c44569' : '#8b8a99' }}>
                        <span></span>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                            <b style={{ color: bodyBytes > MAX_BYTES ? '#c44569' : '#3a3849' }}>{bodyBytes}</b> / {MAX_BYTES} bytes
                        </span>
                    </div>
                </PRow>

                <PRow label="이미지 설정" hint="iOS 10+ / Android 4.1+ 에서 표시됩니다.">
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input
                            type="text"
                            placeholder="직접 이미지의 URL을 입력하거나 파일을 첨부하세요"
                            value={imageUrl}
                            onChange={handleImageUrlChange}
                            disabled={isLocked}
                            style={{ ...inp, flex: 1 }}
                            onFocus={focusOn}
                            onBlur={focusOff}
                        />
                        <label
                            htmlFor="image-upload"
                            style={{
                                height: 42, padding: '0 16px', borderRadius: 10, display: 'inline-flex', alignItems: 'center', gap: 6,
                                border: `1.5px solid ${PURPLE}`, color: PURPLE, background: 'white',
                                fontWeight: 600, fontSize: 13, cursor: isLocked ? 'not-allowed' : 'pointer',
                                whiteSpace: 'nowrap', fontFamily: 'inherit', flexShrink: 0,
                                opacity: isLocked ? 0.5 : 1,
                            }}
                        >
                            <BsImage size={14} /> 파일 선택
                        </label>
                        <input
                            type="file"
                            id="image-upload"
                            style={{ display: 'none' }}
                            accept="image/*"
                            disabled={isLocked}
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                    handleTemplateModification();
                                    setImageUrl(file.name);
                                    handleImageChange?.(e);
                                }
                            }}
                        />
                    </div>
                </PRow>
            </PSection>

            {/* 템플릿 선택 모달 */}
            {showModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 440, boxShadow: '0 8px 40px rgba(60,50,120,.18)', overflow: 'hidden' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f0eff5' }}>
                            <h5 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1f1d2e' }}>템플릿 불러오기</h5>
                            <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7a7889', padding: 4 }}>
                                <BsX size={20} />
                            </button>
                        </div>
                        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                            {templateList && templateList.length > 0 ? (
                                templateList.map(item => (
                                    <div
                                        key={item.id}
                                        onClick={() => handleLoadTemplate(item)}
                                        style={{
                                            padding: '12px 20px', cursor: 'pointer', borderBottom: '1px solid #f4f3f9',
                                            fontSize: 14, color: '#1f1d2e', transition: 'background 100ms',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#f5f4fa'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'white'}
                                    >
                                        {item.templateName}
                                    </div>
                                ))
                            ) : (
                                <div style={{ padding: '24px', textAlign: 'center', color: '#7a7889', fontSize: 14 }}>
                                    템플릿이 없습니다.
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 20px', borderTop: '1px solid #f0eff5' }}>
                            <button
                                onClick={() => setShowModal(false)}
                                style={{ height: 34, padding: '0 16px', borderRadius: 8, border: '1.5px solid #d1d5db', background: 'white', color: '#6b7280', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}
                            >
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TextEditor;
